import React, { useEffect, useState } from 'react';
import { Container, Typography, CircularProgress, Alert, Box, ToggleButtonGroup, ToggleButton, Tooltip, Paper, Slider } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CategoryIcon from '@mui/icons-material/Category';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import walletApi from '../../api/wallet';
import MonthValueDashboard from '../monthDashBoard/MonthValueDashboard';
import CashOutForm from './CashOutForm';
import DialogButton from './../commons/DialogButton';
import CashOutCategoryDashBoard from './CashOutCategoryDashBoard';
import { LAYOUT } from '../../constants/layout';
import { useCurrency } from '../../contexts/CurrencyContext';

const CashOutDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [monthlyTableData, setMonthlyTableData] = useState({});
  const [error, setError] = useState(null);
  const [view, setView] = useState('monthly');
  const [dashboardType, setDashboardType] = useState('monthly');
  const [numRecords, setNumRecords] = useState(12);
  const [chartType, setChartType] = useState('bar');
  const { currency } = useCurrency();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    groupDataByYear();
  }, [data, currency]);

  const fetchData = async () => {
    try {
      const response = await walletApi.get('/cashout/consolidate')
      if (response.status === 200) {
        setData(response.data);
      } else if (response.status === 204) {
        setData([]);
      }
      setLoading(false);
    } catch (err) {
      setError('Não foi possível carregar os dados.');
      setLoading(false);
    }
  };

  const groupDataByYear = () => {
    const groupedData = data.reduce((acc, item) => {
      const [year, month] = item.YearMonth.split('-');
      if (!acc[year]) {
        acc[year] = {
          year,
          months: Array.from({ length: 12 }, () => 0),
          total: 0
        };
      }
      const monthIndex = parseInt(month, 10) - 1;
      const value = parseFloat(item.Currencies[currency]) || 0;
      acc[year].months[monthIndex] += value;
      acc[year].total += value;
      return acc;
    }, {});
    const sortedYears = Object.keys(groupedData).sort((a, b) => b - a);

    const orderedGroupedData = sortedYears.map((year) => ({
      year,
      months: groupedData[year].months,
    }));


    setMonthlyTableData(orderedGroupedData);
  };
  const handleDialogClose = () => {
    fetchData();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={LAYOUT.PAGE_SPACING}>

      <Typography variant="h4" align="center" gutterBottom>
        Custos
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : data.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>Nenhum dado disponível, cadastre um novo custo.</Alert>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderRadius: 1, p: 0.5 }}>
                <ToggleButtonGroup
                  value={dashboardType}
                  exclusive
                  onChange={(e, newType) => newType && setDashboardType(newType)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: 1,
                      px: 1.5,
                      gap: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                      },
                    },
                  }}
                >
                  <Tooltip title="Visão por Período">
                    <ToggleButton value="monthly">
                      <CalendarMonthIcon fontSize="small" />
                      Período
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="Visão por Categoria">
                    <ToggleButton value="category">
                      <CategoryIcon fontSize="small" />
                      Categoria
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>

                <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 1 }} />

                <ToggleButtonGroup
                  value={chartType}
                  exclusive
                  onChange={(e, newType) => newType && setChartType(newType)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: 1,
                      px: 1.5,
                      '&.Mui-selected': {
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                      },
                    },
                  }}
                >
                  <Tooltip title="Gráfico de Linhas">
                    <ToggleButton value="line">
                      <ShowChartIcon fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="Gráfico de Barras">
                    <ToggleButton value="bar">
                      <BarChartIcon fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 200, maxWidth: 400 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {numRecords} meses
                </Typography>
                <Slider
                  value={numRecords}
                  onChange={(e, newValue) => setNumRecords(newValue)}
                  valueLabelDisplay="auto"
                  min={1}
                  max={24}
                  step={1}
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Paper>

          {dashboardType === 'monthly' ? (
            <MonthValueDashboard
              data={data}
              currency={currency}
              view={view}
              setView={setView}
              monthlyDataByYear={monthlyTableData}
              numRecords={numRecords}
              chartType={chartType}
            />
          ) : (
            <CashOutCategoryDashBoard
              currency={currency}
              numRecords={numRecords}
              chartType={chartType}
            />
          )}
        </>
      )}
      <DialogButton
        buttonText="Cadastrar Novo Custo"
        onClose={handleDialogClose}
      >
        <CashOutForm />
      </DialogButton>
    </Container>
  );
};

export default CashOutDashboard;
